import { HttpClient } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';

import { CelumPropertiesProvider } from '@celum/core';
import { SpecificationList, TenantSpecificationAssignment, TenantSpecificationAssignmentList } from '@celum/formats/domain';

import { FormatsContextAware } from './formats-context-aware';

export const ASSIGNMENTS_SERVICE = new InjectionToken<AssignmentsService>('ASSIGNMENTS_SERVICE');

/**
 * Single implementation of specification-assignment API calls shared by the standalone `formats` app and the
 * `formats-embed` app. The environment-specific concerns (apiUrl/tenant resolution, authentication headers for
 * embedded calls) are handled by `FormatsContextAware` based on the `FORMATS_CONTEXT` provided where this service
 * is registered. Assignments are always tenant-scoped (via `/configurations/{tenantId}`), regardless of whether the
 * current user is an admin, so this service resolves its own apiUrl rather than using `FormatsContextAware.apiUrl`.
 */
@Injectable()
export class AssignmentsService extends FormatsContextAware {
  private http = inject(HttpClient);

  public getTenantAssignments(specificationIds: number[]): Observable<TenantSpecificationAssignmentList> {
    return this.withContext(context =>
      this.http.get<TenantSpecificationAssignmentList>(`${this.tenantApiUrl}/assignments`, {
        params: {
          specificationIds
        },
        context
      })
    );
  }

  /**
   * Retrieves the list assigned or assignable specifications for the current tenant with pagination.
   *
   * @param page The page number for pagination.
   * @param size The number of items per page.
   * @param onlyAssignableSpecifications Whether to filter only assignable specifications (default: false).
   * @returns Observable emitting the list of specifications.
   */
  public getTenantSpecifications(page: number, size: number, onlyAssignableSpecifications = false): Observable<SpecificationList> {
    return this.withContext(context =>
      this.http.get<SpecificationList>(`${this.tenantApiUrl}/specifications`, {
        params: {
          page,
          size,
          onlyAssignableSpecifications
        },
        context
      })
    );
  }

  public assignSpecifications(specIds: number[]): Observable<TenantSpecificationAssignment[]> {
    return this.withContext(context =>
      this.http.post<TenantSpecificationAssignment[]>(
        `${this.tenantApiUrl}/assignments`,
        {
          specificationReferences: {
            ids: specIds
          }
        },
        { context }
      )
    );
  }

  public unassignSpecification(specId: number): Observable<void> {
    return this.withContext(context => this.http.delete<void>(`${this.tenantApiUrl}/assignments/${specId}`, { context }));
  }

  public patchAssignment(assignmentId: number, useTenantEnvironment: boolean): Observable<TenantSpecificationAssignment> {
    return this.withContext(context =>
      this.http.patch<TenantSpecificationAssignment>(`${this.tenantApiUrl}/assignments/${assignmentId}`, { useTenantEnvironment }, { context })
    );
  }

  private get tenantApiUrl(): string {
    return `${CelumPropertiesProvider.properties.appProperties.formats.apiUrl}/configurations/${this.getTenantId()}`;
  }
}
